<template>
  <div class="main-page">
    <template v-if="showHeader">
      <section class="page-section mb-3">
        <div class="container-fluid">
          <div class="grid justify-content-between">
            <div class="col-12 md:col-5 lg:col-4">
              <div class="">
                <template v-if="$auth.canView('/user_permissions/add')">
                  <Button
                    label="Add Role Permissions"
                    @click="
                      app.openPageDialog({
                        page: 'user_permissions/add',
                        url: `/user_permissions/add`,
                        closeBtn: true,
                        persistent: true,
                        position: 'bottom',
                        width: '30%',
                      })
                    "
                    class="bg-primary p-button-rounded"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
    <section class="page-section">
      <div class="container-fluid">
        <div class="grid">
          <div class="col comp-grid">
            <div class="">
              <div class="flex align-items-center justify-content-around">
                <div v-if="searchText" :class="filterTagClass">
                  Search
                  <Chip
                    class="font-medium px-2 py-1"
                    removable
                    @remove="clearSearch()"
                    >{{ searchText }}</Chip
                  >
                </div>
              </div>
              <div class="">
                <div>
                  <div class="grid justify-content-start">
                    <!-- Master Page Start -->
                    <div class="col-12">
                      <div class="" :class="{ card: !isSubPage }">
                        <!-- page records template -->
                        <div class="page-records" v-if="!loading">
                          <DataTable
                            :lazy="true"
                            :loading="loading"
                            v-model:selection="selectedItems"
                            :value="records"
                            dataKey="permission_id"
                            @sort="onSort($event)"
                            class=""
                            :showGridlines="false"
                            :rowHover="true"
                            responsiveLayout="stack"
                          >
                            <Column
                              selectionMode="multiple"
                              headerStyle="width: 3em"
                            ></Column>
                            <Column
                              field="permission"
                              header="Page Access Permission"
                            >
                              <template #body="{ data }">
                                {{ data.permission }}
                              </template>
                            </Column>
                            <Column
                              headerStyle="width: 3em"
                              headerClass="text-center"
                            >
                              <template #body="{ data }">
                                <Menubar
                                  class="p-0"
                                  ref="actionMenu"
                                  :model="getActionMenuModel(data)"
                                />
                              </template>
                            </Column>
                          </DataTable>
                        </div>
                        <!-- page loading indicator -->
                        <template v-if="loading">
                          <div>
                            <DataTable
                              :value="[1, 2, 3, 4, 5]"
                              responsiveLayout="scroll"
                            >
                              <Column style="width: 3rem">
                                <template #body>
                                  <Skeleton shape="circle" size="2rem" />
                                </template>
                              </Column>
                              <Column>
                                <template #body>
                                  <Skeleton />
                                </template>
                              </Column>
                              <Column>
                                <template #body>
                                  <Skeleton borderRadius="16px" />
                                </template>
                              </Column>
                              <Column>
                                <template #body>
                                  <Skeleton />
                                </template>
                              </Column>
                              <Column style="width: 3rem">
                                <template #body>
                                  <Skeleton size="2rem" />
                                </template>
                              </Column>
                            </DataTable>
                          </div>
                        </template>
                        <!-- end of page loading indicator-->
                        <!-- Empty record -->
                        <template v-if="pageReady && !records.length">
                          <div
                            class="p-3 my-3 text-500 text-lg font-medium text-center"
                          >
                            No record found
                          </div>
                        </template>
                        <!-- end of empty record-->
                        <!-- pagination component-->
                        <template v-if="showFooter">
                          <div class="">
                            <div class="flex justify-content-between">
                              <div class="flex-grow-0">
                                <div class="flex">
                                  <template
                                    v-if="
                                      $auth.canView('user_permissions/delete')
                                    "
                                  >
                                    <div
                                      v-if="selectedItems.length"
                                      class="m-2"
                                    >
                                      <Button
                                        @click="deleteItem(selectedItems)"
                                        icon="pi pi-trash"
                                        class="p-button-danger"
                                        title="Delete Selected"
                                      />
                                    </div>
                                  </template>
                                </div>
                              </div>
                              <div class="flex-grow-1">
                                <div
                                  v-if="pageReady && totalPages > 1"
                                  class="flex justify-content-center"
                                >
                                  <div class="m-2">
                                    <Button
                                      :disabled="pagination.page <= 1"
                                      label="Previous"
                                      @click="loadPreviousPage()"
                                      icon="pi pi-arrow-left"
                                    />
                                  </div>
                                  <div class="m-2">
                                    <Button
                                      :disabled="pagination.page >= totalPages"
                                      label="Next"
                                      @click="loadNextPage()"
                                      icon="pi pi-arrow-right"
                                      icon-pos="right"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <!-- end of pagination component-->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { reactive, toRefs, onMounted } from "vue";
import { useApp } from "@/composables/app.js";
import { useAuth } from "@/composables/auth";
import { useListPage } from "@/composables/listpage.js";
//[--PAGE-IMPORT-STATEMENT--]
const props = defineProps({
  primaryKey: {
    type: String,
    default: "permission_id",
  },
  pageName: {
    type: String,
    default: "user_permissions",
  },
  routeName: {
    type: String,
    default: "user_permissionslist",
  },
  apiPath: {
    type: String,
    default: "user_permissions/index",
  },
  paginate: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  showBreadcrumbs: {
    type: Boolean,
    default: true,
  },
  exportButton: {
    type: Boolean,
    default: true,
  },
  importButton: {
    type: Boolean,
    default: false,
  },
  multiCheckbox: {
    type: Boolean,
    default: true,
  },
  emptyRecordMsg: {
    type: String,
    default: "No record found",
  },
  titleBeforeDelete: {
    type: String,
    default: "Delete record",
  },
  msgBeforeDelete: {
    type: String,
    default: "Are you sure you want to delete this record?",
  },
  msgAfterDelete: {
    type: String,
    default: "Record deleted successfully",
  },
  page: {
    type: Number,
    default: 1,
  },
  limit: {
    type: Number,
    default: 10,
  },
  mergeRecords: {
    // for infinite loading
    type: Boolean,
    default: false,
  },
  search: {
    type: String,
    default: "",
  },
  fieldName: null,
  fieldValue: null,
  sortBy: {
    type: String,
    default: "",
  },
  sortType: {
    type: String,
    default: "", //desc or asc
  },
  isSubPage: {
    type: Boolean,
    default: false,
  },
  filterTagClass: {
    type: String,
    default:
      "surface-card p-2 text-500 flex-grow-1 text-center m-1 mb-3 flex-grow-1 text-center",
  },
});
const app = useApp();
const auth = useAuth();
const filters = reactive({});
//init list page hook
const page = useListPage(props, filters);
//page state
const {
  totalRecords, // total records from api - Number
  recordCount, // current record count - Number
  loading, // Api loading state - Boolean
  selectedItems, // Data table selected items -Array
  pagination, //Pagination object - Object
  searchText, // search text - String
  pageReady, // api data loaded successfully
} = toRefs(page.state);
//page computed propties
const {
  records, // page record from store - Array
  apiUrl, // current api path - URL
  currentRecord, // master detail selected record - Object
  pageBreadCrumb, // get page navigation paths - Object
  canLoadMore, // if api has more data for loading - Boolean
  finishedLoading, // if api has finished loading - Boolean
  totalPages, // total number of pages from api - Number
  recordsPosition, // current record position from api - Number
} = page.computedProps;
//page methods
const {
  load, // load data from api
  reload, // reset pagination and load data
  loadNextPage, // load next page
  loadPreviousPage, // load previous page
  exportPage, // export page records - args = (exportFormats, apiUrl, pageName)
  clearSearch, // clear input search
  debounce, // debounce input search
  onSort, // reset page number before sort from api
  deleteItem, // delete item by id or selected items - args = (id) or (selectedItems)
  setCurrentRecord, // master detail set current record
  isCurrentRecord, // master detail
  removeFilter, // remove page filter item - args = (filter.propertyname)
  getFilterLabel, // get filter item display label - args = (filter.propertyname)
  filterHasValue, //check if filter item has value - args = (filter.propertyname)
  importComplete, // reload page after data import
} = page.methods;
function getActionMenuModel(data) {
  return [
    {
      label: "Delete",
      command: (event) => {
        deleteItem(data.permission_id);
      },
      icon: "pi pi-minus-circle",
      visible: auth.canView("user_permissions/delete"),
    },
  ];
}
onMounted(() => {
  const pageTitle = "User Permissions";
  app.setPageTitle(props.routeName, pageTitle);
  load();
});
</script>
<style scoped></style>
<style scoped></style>
